import { Listbox, Portal } from "@headlessui/react";
import { mdiChevronDown, mdiLoading } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { getClassByVariant } from "helpers/getClassByVariant";
import type { IOption } from "models/Form";
import styles from "./style.module.scss";
import { FieldError } from "react-hook-form";
import ScrollHint from "../../hooks/ScrollHint";
import { useColorScheme } from "../../hooks/useColorScheme";

const MIN_AVAILABLE_SPACE = 160;
const PORTAL_Z_INDEX = 9999;
const MENU_OFFSET = 8;
const VIEWPORT_GUTTER = 8;

export interface IProps {
  id?: string;
  value?: IOption | null;
  onChange?: (value: IOption) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  options: Array<IOption>;
  label?: string | React.ReactNode;
  className?: string;
  labelClass?: string;
  btnClass?: string;
  inputClass?: string;
  arrowClass?: string;
  dropdownClass?: string;
  dropdownContainerClass?: string;
  dropdownMaxHeight?: number;
  usePortal?: boolean;
  error?: FieldError;
  color?: "primary" | "secondary";
  direction?: "up" | "down";
  autoDirection?: boolean;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  loading?: boolean;
  style?: Record<any, any>;
}

type Direction = "up" | "down";

export default function Select({
  id,
  options,
  value = null,
  onChange,
  onBlur,
  label,
  className,
  color = "primary",
  labelClass,
  btnClass,
  inputClass,
  arrowClass,
  dropdownClass,
  dropdownContainerClass = "",
  dropdownMaxHeight = 300,
  usePortal = true,
  direction = "down",
  autoDirection = true,
  error,
  placeholder,
  disabled,
  required = false,
  loading = false,
  style
}: Readonly<IProps>) {
  const placeholderItem = useMemo<IOption>(
    () => ({
      id: -1,
      label: placeholder || "Select an option",
      value: "",
      isDisabled: true
    }),
    [placeholder]
  );
  const selectedOption = value || placeholderItem;
  const { currentIsDark } = useColorScheme();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuNode, setMenuNode] = useState<HTMLElement | null>(null);

  const [menuState, setMenuState] = useState<{
    left: number;
    top?: number;
    bottom?: number;
    buttonTop: number;
    width: number;
    direction: Direction;
    maxHeight: number;
  }>({
    left: 0,
    top: 0,
    buttonTop: 0,
    width: 0,
    direction: direction,
    maxHeight: dropdownMaxHeight
  });

  const getUsableBand = useCallback(() => {
    const visual = window.visualViewport;
    const offsetTop = visual ? visual.offsetTop : 0;
    const height = visual ? visual.height : window.innerHeight;
    const rootStyle = getComputedStyle(document.documentElement);
    // useSafeArea publishes the measured Android insets as --safe-area-*-fixed; the
    // --safe-area-inset-* names are only ever written as a zeroed fallback.
    const inset = (...names: string[]) => {
      for (const name of names) {
        const value = parseFloat(rootStyle.getPropertyValue(name));
        if (Number.isFinite(value) && value > 0) return value;
      }
      return 0;
    };

    return {
      top: offsetTop + inset("--safe-area-top-fixed", "--safe-area-inset-top") + VIEWPORT_GUTTER,
      bottom:
        offsetTop +
        height -
        inset("--safe-area-bottom-fixed", "--safe-area-inset-bottom") -
        VIEWPORT_GUTTER
    };
  }, []);

  const updateMenuMetrics = useCallback(() => {
    if (typeof window === "undefined" || !buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const band = getUsableBand();

    const availableBelow = band.bottom - rect.bottom;
    const availableAbove = rect.top - MENU_OFFSET - band.top;

    let effectiveDirection: Direction = direction;
    if (autoDirection) {
      const preferred = direction === "up" ? availableAbove : availableBelow;
      const alternative = direction === "up" ? availableBelow : availableAbove;
      const preferredFits = preferred >= dropdownMaxHeight;
      const alternativeFits = alternative >= dropdownMaxHeight;
      const preferredIsCramped = preferred < MIN_AVAILABLE_SPACE && alternative > preferred;
      if (!preferredFits && (alternativeFits || preferredIsCramped)) {
        effectiveDirection = direction === "up" ? "down" : "up";
      }
    }

    const room = effectiveDirection === "down" ? availableBelow : availableAbove;
    const computedMaxHeight = Math.min(dropdownMaxHeight, Math.max(0, room));

    // Anchored by the edge the menu grows from, never by a percentage transform:
    // translateY(-100%) resolves against the element's own height, and the wrapper
    // measures 0 in the WebView, so the menu never moved and opened downward.
    const anchor =
      effectiveDirection === "down"
        ? { top: rect.bottom }
        : { bottom: Math.max(0, window.innerHeight - (rect.top - MENU_OFFSET)) };

    setMenuState({
      left: rect.left,
      ...anchor,
      buttonTop: rect.top,
      width: rect.width,
      direction: effectiveDirection,
      maxHeight: computedMaxHeight
    });
  }, [direction, dropdownMaxHeight, autoDirection, getUsableBand]);

  // Applied to the node rather than through className or the style prop: the WebView keeps
  // resolving .selectList's `absolute` and `w-full` over both, even against a doubled class,
  // which left the menu full-viewport width. An important inline declaration cannot be outranked.
  const applyPlacement = useCallback(() => {
    if (!usePortal || !menuNode) return;

    const style = menuNode.style;
    style.setProperty("position", "fixed", "important");
    style.setProperty("left", `${menuState.left}px`, "important");
    style.setProperty("width", `${menuState.width}px`, "important");

    if (menuState.top !== undefined) {
      style.setProperty("top", `${menuState.top}px`, "important");
      style.removeProperty("bottom");
    } else {
      style.setProperty("bottom", `${menuState.bottom ?? 0}px`, "important");
      style.removeProperty("top");
    }
  }, [usePortal, menuNode, menuState]);

  const clampMenuIntoView = useCallback(() => {
    if (!usePortal || !menuNode) return;

    const band = getUsableBand();
    const rect = menuNode.getBoundingClientRect();
    if (rect.height > band.bottom - band.top) return;

    let shift = 0;
    if (rect.bottom > band.bottom) shift = band.bottom - rect.bottom;
    if (rect.top + shift < band.top) shift = band.top - rect.top;
    if (Math.abs(shift) < 1) return;

    setMenuState((previous) =>
      previous.top !== undefined
        ? { ...previous, top: previous.top + shift }
        : { ...previous, bottom: (previous.bottom ?? 0) - shift }
    );
  }, [usePortal, getUsableBand, menuNode]);

  const listWrapperClasses = useMemo(
    () =>
      clsx(
        "rounded-lg border border-card shadow-lg !bg-input",
        currentIsDark && "border border-gray-600 text-gray-100",
        dropdownContainerClass
      ),
    [currentIsDark, dropdownContainerClass]
  );

  const optionClasses = useMemo(
    () =>
      clsx(
        "block rounded-lg px-3 py-2 my-0.5 text-sm leading-snug transition-colors duration-150",
        dropdownClass
      ),
    [dropdownClass]
  );

  const renderOptions = (positioned?: boolean) => (
    <Listbox.Options
      ref={positioned ? setMenuNode : undefined}
      static
      className={clsx("nested-scroll-listbox", listWrapperClasses, styles.selectList)}
      style={positioned ? { pointerEvents: "auto" } : undefined}>
      <ScrollHint darkMode={currentIsDark} maxHeight={menuState.maxHeight}>
        {options
          .filter((item) => {
            // Show enabled options
            if (!item.isDisabled) return true;
            // Show disabled options only if they are currently selected
            return value && value.id === item.id;
          })
          .map((item) => (
            <Listbox.Option
              key={item.id}
              value={item}
              disabled={item.isDisabled}
              className={({ active, disabled }) =>
                clsx(
                  optionClasses,
                  value?.id === item.id &&
                    (currentIsDark ? "bg-input text-white" : "bg-primary/10 text-primary"),
                  active && !disabled && (currentIsDark ? "bg-inputDark" : "bg-placeholder/40"),
                  disabled && "cursor-not-allowed",
                  disabled && (currentIsDark ? "bg-inputDark" : "bg-placeholder/40")
                )
              }>
              {item.label}
            </Listbox.Option>
          ))}
      </ScrollHint>
    </Listbox.Options>
  );

  const portalContent = (
    <Portal>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          zIndex: PORTAL_Z_INDEX
        }}>
        {renderOptions(true)}
      </div>
    </Portal>
  );

  const inlineContent = (
    <div
      className={clsx(
        styles.inlineMenu,
        menuState.direction === "up" ? styles.inlineMenuUp : styles.inlineMenuDown
      )}>
      {renderOptions()}
    </div>
  );

  return (
    <div style={style} className={clsx(styles.selectContainer, className)}>
      {label && (
        <label
          htmlFor={id}
          className={clsx(styles.selectLabel, labelClass, getClassByVariant(color)?.label)}>
          {required ? "* " : ""}
          {label}
        </label>
      )}

      <Listbox
        value={selectedOption}
        onChange={onChange}
        onBlur={onBlur}
        as="div"
        className={clsx(styles.selectWrapper, !!error && "!border-b !border-b-error !rounded-base")}
        disabled={disabled}>
        {({ open }) => {
          useLayoutEffect(() => {
            if (open) updateMenuMetrics();
          }, [open, updateMenuMetrics]);

          useEffect(() => {
            if (!open) return;
            const handler = () => updateMenuMetrics();
            const visual = window.visualViewport;
            window.addEventListener("resize", handler);
            window.addEventListener("scroll", handler, true);
            visual?.addEventListener("resize", handler);
            visual?.addEventListener("scroll", handler);
            return () => {
              window.removeEventListener("resize", handler);
              window.removeEventListener("scroll", handler, true);
              visual?.removeEventListener("resize", handler);
              visual?.removeEventListener("scroll", handler);
            };
          }, [open, updateMenuMetrics]);

          useLayoutEffect(() => {
            if (!open) return;
            applyPlacement();
            clampMenuIntoView();
          }, [open, applyPlacement, clampMenuIntoView, menuState]);

          return (
            <>
              <Listbox.Button
                id={id}
                ref={buttonRef}
                className={clsx(
                  styles.selectButton,
                  getClassByVariant(color)?.input,
                  btnClass,
                  inputClass,
                  !!disabled && "cursor-not-allowed disabled:bg-inputDark",
                  !value && "!text-placeholder"
                )}>
                {loading ? (
                  <div className="flex justify-end">
                    <Icon path={mdiLoading} spin={1} size={0.5} />
                  </div>
                ) : (
                  <>
                    {selectedOption?.label}
                    <Icon path={mdiChevronDown} className={clsx(styles.selectArrow, arrowClass)} />
                  </>
                )}
              </Listbox.Button>

              {!loading && open && (usePortal ? portalContent : inlineContent)}
            </>
          );
        }}
      </Listbox>

      {error && <span className="text-sm leading-5 text-error">{error?.message}</span>}
    </div>
  );
}
